import React, { useState } from 'react';
import { Plus, Trash2, Loader2, Tag, Pencil, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { COLOR_PRESETS, inputClass, inputStyle } from './constants';

export function CategorieTab() {
  const { customCategories, createCategory, deleteCategory, updateCategoryColor } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', color: COLOR_PRESETS[0].color, bg_color: COLOR_PRESETS[0].bg, description: '' });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingColorId, setEditingColorId] = useState(null);
  const [editColor, setEditColor] = useState({ color: '', bg_color: '' });
  const [savingColor, setSavingColor] = useState(false);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Inserisci un nome per la categoria'); return; }
    setSaving(true);
    try {
      await createCategory(form);
      toast.success(`Categoria "${form.name}" creata`);
      setForm({ name: '', color: COLOR_PRESETS[0].color, bg_color: COLOR_PRESETS[0].bg, description: '' });
      setShowForm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare la categoria "${name}"?`)) return;
    setDeletingId(id);
    try { await deleteCategory(id); toast.success('Categoria eliminata'); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore nell'eliminazione"); }
    finally { setDeletingId(null); }
  };

  const openEditColor = (cat) => {
    setEditingColorId(cat.id);
    setEditColor({ color: cat.color, bg_color: cat.bg_color });
  };

  const handleSaveColor = async (catId) => {
    setSavingColor(true);
    try {
      await updateCategoryColor(catId, editColor.color, editColor.bg_color);
      toast.success('Colore aggiornato');
      setEditingColorId(null);
    } catch { toast.error('Errore nel salvataggio colore'); }
    finally { setSavingColor(false); }
  };

  const selectColor = (preset) => setForm(prev => ({ ...prev, color: preset.color, bg_color: preset.bg }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Gestisci le categorie per organizzare le tue email.</p>
        <button data-testid="add-category-btn" onClick={() => setShowForm(s => !s)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border transition-all" style={{ borderColor: showForm ? '#E2725B' : '#D9D2C8', background: showForm ? 'rgba(226,114,91,0.08)' : 'white', color: showForm ? '#E2725B' : '#333' }}>
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? 'Annulla' : 'Nuova categoria'}
        </button>
      </div>

      {showForm && (
        <div className="rounded-xl border p-5 space-y-4" style={{ borderColor: '#D9D2C8', background: 'white' }}>
          <h4 className="text-sm font-semibold">Nuova categoria</h4>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Nome *</label>
            <input data-testid="new-category-name" type="text" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Es. Fornitori" className={inputClass} style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Descrizione</label>
            <input data-testid="new-category-desc" type="text" value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Opzionale" className={inputClass} style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-2">Colore</label>
            <div className="flex items-center gap-3">
              <div className="flex gap-2 flex-wrap">
                {COLOR_PRESETS.map((preset, i) => (
                  <button key={i} data-testid={`color-preset-${i}`} onClick={() => selectColor(preset)} className="w-7 h-7 rounded-full border-2 transition-all hover:scale-110" style={{ background: preset.color, borderColor: form.color === preset.color ? '#333' : 'transparent' }} title={preset.color} />
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="w-8 h-8 rounded-lg" style={{ background: form.bg_color, border: `2px solid ${form.color}` }} />
                <span className="text-xs font-mono">{form.color}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button data-testid="save-category-btn" onClick={handleCreate} disabled={saving} className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-xl transition-all" style={{ background: '#E2725B', opacity: saving ? 0.7 : 1 }}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              {saving ? 'Salvataggio...' : 'Crea categoria'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {customCategories.map(cat => (
          <div key={cat.id} data-testid={`category-item-${cat.id}`} className="rounded-xl border" style={{ borderColor: '#D9D2C8', background: 'white' }}>
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: cat.bg_color }}>
                  <Tag size={14} style={{ color: cat.color }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{cat.name}</span>
                    {cat.is_default === 1 && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#F0F2F3', color: '#7F8C8D' }}>predefinita</span>
                    )}
                  </div>
                  {cat.description && <p className="text-xs text-gray-400">{cat.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full cursor-pointer hover:scale-110 transition-transform" style={{ background: cat.color }} title="Cambia colore" onClick={() => editingColorId === cat.id ? setEditingColorId(null) : openEditColor(cat)} />
                <button data-testid={`edit-color-${cat.id}`} onClick={() => editingColorId === cat.id ? setEditingColorId(null) : openEditColor(cat)} className="p-1.5 text-gray-400 hover:text-gray-700 transition-colors rounded-lg" title="Modifica colore">
                  <Pencil size={13} />
                </button>
                {cat.is_default !== 1 && (
                  <button data-testid={`delete-category-${cat.id}`} onClick={() => handleDelete(cat.id, cat.name)} disabled={deletingId === cat.id} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg">
                    {deletingId === cat.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                )}
              </div>
            </div>
            {editingColorId === cat.id && (
              <div className="border-t px-4 pb-4 pt-3 space-y-2" style={{ borderColor: '#F0ECE8', background: 'hsl(40,18%,98%)' }}>
                <p className="text-xs font-medium text-gray-500">Scegli colore per <strong>{cat.name}</strong>:</p>
                <div className="flex items-center gap-3 flex-wrap">
                  {COLOR_PRESETS.map((preset, i) => (
                    <button key={i} data-testid={`edit-color-preset-${cat.id}-${i}`} onClick={() => setEditColor({ color: preset.color, bg_color: preset.bg })} className="w-7 h-7 rounded-full border-2 transition-all hover:scale-110" style={{ background: preset.color, borderColor: editColor.color === preset.color ? '#333' : 'transparent' }} />
                  ))}
                  <div className="w-7 h-7 rounded-lg" style={{ background: editColor.bg_color, border: `2px solid ${editColor.color}` }} />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button data-testid={`save-color-${cat.id}`} onClick={() => handleSaveColor(cat.id)} disabled={savingColor} className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white rounded-lg" style={{ background: '#E2725B' }}>
                    {savingColor ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                    Salva colore
                  </button>
                  <button onClick={() => setEditingColorId(null)} className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50" style={{ borderColor: '#D9D2C8' }}>Annulla</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {customCategories.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Nessuna categoria configurata</p>}
      </div>
    </div>
  );
}
